import LegalLayout from './LegalLayout';

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      updated="May 2026"
      testId="terms-page"
      notice={
        <>
          These Terms of Service govern access to Casa de Candy websites, communications, appointment
          inquiries, customer-support interactions, and related business services. Casa de Candy
          operates as a lawful, adult-only, time-based private hospitality and companionship-style
          appointment business.
        </>
      }
    >
      <div className="legal-section">
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing the website, clicking through any consent flow, submitting information, calling,
          texting, messaging through WhatsApp, requesting availability, booking an appointment, visiting
          a location, or otherwise interacting with Casa de Candy, you agree to these Terms of Service,
          the Privacy Policy, and the Non-Disclosure Agreement, each incorporated by reference.
        </p>
        <p>
          If you do not agree, do not use the website, submit information, request an appointment, or
          continue communication.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Adult-Only Eligibility</h2>
        <p>
          Casa de Candy is intended only for adults who are at least 18 years of age. By using the
          website or communicating with Casa de Candy, you represent that you are at least 18 years old
          and legally permitted to enter into these Terms.
        </p>
        <p>
          Casa de Candy reserves the right to request age verification, identification, or additional
          confirmation where necessary for safety, compliance, or operational reasons.
        </p>
      </div>

      <div className="legal-section">
        <h2>3. Nature of Business</h2>
        <p>
          Casa de Candy provides private, adult, time-based appointment coordination, ambiance,
          hospitality, conversation, companionship-style experiences, and related customer-support
          communications. All appointments, sessions, or experiences are based on reserved time,
          availability, lawful presence, lawful interaction, and access to a private environment.
        </p>
        <p>
          <strong>
            No unlawful, explicit, sexual, coercive, or prohibited service is offered, requested,
            promised, negotiated, sold, purchased, or guaranteed by Casa de Candy.
          </strong>{' '}
          Any user who attempts to request, pressure, negotiate, imply, demand, or engage in unlawful
          conduct may be refused service, blocked, removed, reported where appropriate, and permanently
          denied future access.
        </p>
      </div>

      <div className="legal-section">
        <h2>4. No Guarantee of Availability or Access</h2>
        <p>
          Submitting an inquiry, sending a message, calling, completing a form, or entering a consent
          flow does not guarantee acceptance, appointment availability, confirmation, access, location
          disclosure, or continued communication. Casa de Candy may accept, decline, cancel, reschedule,
          or discontinue any interaction at its discretion.
        </p>
      </div>

      <div className="legal-section">
        <h2>5. Booking and Confirmation</h2>
        <p>
          Appointments may require confirmation of requested time, session duration, contact
          information, arrival timing, payment method, and agreement to applicable policies. Casa de
          Candy may withhold exact location details, parking instructions, entry instructions, access
          codes, or host details until confirmation steps are completed.
        </p>
        <p>
          Users are responsible for providing accurate information. False information, misleading
          communication, harassment, disrespect, unsafe behavior, or refusal to follow instructions may
          result in denial of service.
        </p>
      </div>

      <div className="legal-section">
        <h2>6. Payment Terms</h2>
        <p>
          Payments are for reserved time, private ambiance, hospitality, coordination, customer support,
          and lawful appointment access only. Fees do not purchase, imply, promise, or guarantee any
          specific personal conduct, physical contact, outcome, illegal act, or prohibited service.
        </p>
        <p>
          Payment terms, accepted payment methods, deposits, timing, cancellation consequences, and
          appointment requirements may vary by appointment type, availability, risk, location, or
          operational needs. Casa de Candy may refuse, cancel, or decline appointments for payment
          issues, chargebacks, fraud concerns, or policy violations.
        </p>
      </div>

      <div className="legal-section">
        <h2>7. Cancellation, Delay, and No-Show Policy</h2>
        <p>
          Users are expected to provide reasonable notice for cancellations, delays, or changes.
          Repeated cancellations, no-shows, late arrivals, false bookings, failure to confirm, or
          failure to follow arrival instructions may result in refusal of future appointments,
          forfeiture of priority, or additional confirmation requirements.
        </p>
      </div>

      <div className="legal-section">
        <h2>8. Conduct Standards</h2>
        <p>Users must behave lawfully, respectfully, safely, and discreetly at all times. Prohibited conduct includes, but is not limited to:</p>
        <ul>
          <li>Harassment, threats, intimidation, coercion, stalking, or aggressive behavior;</li>
          <li>Requests, pressure, negotiation, or discussion involving unlawful or prohibited conduct;</li>
          <li>Unauthorized photography, video, audio recording, screenshots, livestreaming, or screen recording;</li>
          <li>Sharing private communications, location information, access instructions, or identifying details;</li>
          <li>Arriving intoxicated, disruptive, unsafe, or accompanied by unauthorized persons;</li>
          <li>Attempting to bypass screening, consent flows, payment requirements, privacy rules, or safety procedures;</li>
          <li>Misuse of SMS, WhatsApp, phone, forms, website systems, or appointment channels.</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>9. Right to Refuse or Terminate Service</h2>
        <p>
          Casa de Candy reserves the right to refuse service, terminate communication, cancel
          appointments, revoke access, block numbers, ban users, preserve evidence, or involve
          appropriate authorities where necessary for safety, privacy, policy enforcement, fraud
          prevention, legal compliance, operational discretion, or business judgment.
        </p>
      </div>

      <div className="legal-section">
        <h2>10. Privacy, Discretion, and Confidentiality</h2>
        <p>
          All users agree to respect the privacy, discretion, and confidentiality of Casa de Candy, its
          personnel, contractors, hosts, visitors, customers, locations, communications, procedures,
          pricing, access instructions, and operations. The Casa de Candy Non-Disclosure Agreement is
          incorporated into these Terms and applies to all interactions.
        </p>
      </div>

      <div className="legal-section">
        <h2>11. SMS, WhatsApp, and Phone Consent</h2>
        <p>
          By submitting your phone number, checking a consent box, initiating communication, calling,
          texting, messaging through WhatsApp, or continuing communication, you consent to receive
          communications from Casa de Candy related to inquiries, appointment coordination, account
          notifications, customer support, and limited promotional updates.
        </p>
        <ul>
          <li>Message frequency may vary.</li>
          <li>Msg &amp; data rates may apply.</li>
          <li>Reply STOP to opt out.</li>
          <li>Reply HELP for assistance.</li>
        </ul>
        <p>
          Consent to receive marketing messages is not required as a condition of purchase where
          prohibited by law. Opt-out requests will be honored in accordance with applicable law and
          carrier requirements.
        </p>
      </div>

      <div className="legal-section">
        <h2>12. Website and Platform Use</h2>
        <p>
          Users may not attempt to copy, scrape, reverse engineer, disrupt, overload, hack, bypass,
          misuse, or interfere with Casa de Candy websites, forms, communication systems, automation
          systems, booking flows, security tools, or third-party integrations.
        </p>
      </div>

      <div className="legal-section">
        <h2>13. Reviews, Public Statements, and Confidential Information</h2>
        <p>
          Casa de Candy does not prohibit lawful, honest, non-confidential opinions where protected by
          law. However, users may not publish false statements, defamatory statements, threats,
          harassment, doxxing, private communications, screenshots, recordings, location details, access
          instructions, identifying details of personnel or visitors, or confidential operational
          information.
        </p>
      </div>

      <div className="legal-section">
        <h2>14. Assumption of Responsibility</h2>
        <p>
          Users are responsible for their own conduct, communication, transportation, personal
          belongings, payment choices, device security, and compliance with these Terms. Users
          acknowledge that private appointment coordination requires discretion, safety, and mutual
          respect.
        </p>
      </div>

      <div className="legal-section">
        <h2>15. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, Casa de Candy, its owners, personnel, contractors,
          affiliates, and service providers are not liable for indirect, incidental, consequential,
          special, exemplary, or punitive damages, including damages related to communication errors,
          scheduling issues, platform outages, third-party services, user conduct, unauthorized
          disclosures, denied access, canceled appointments, or misuse of services.
        </p>
        <p>Nothing in these Terms limits liability where such limitation is prohibited by law.</p>
      </div>

      <div className="legal-section">
        <h2>16. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Casa de Candy, its owners, personnel,
          contractors, affiliates, and service providers from claims, losses, liabilities, damages,
          costs, and expenses arising from your violation of these Terms, misuse of the website or
          communications, unlawful conduct, breach of confidentiality, unauthorized recording or
          disclosure, false statements, payment disputes, or harm caused by your actions.
        </p>
      </div>

      <div className="legal-section">
        <h2>17. Dispute Resolution</h2>
        <p>
          Before filing any claim, users agree to make a good-faith effort to resolve disputes by
          contacting Casa de Candy at support@casadecandy.com. Casa de Candy may require written notice
          describing the issue, requested resolution, and supporting information.
        </p>
        <p>
          To the extent permitted by law, disputes shall be governed by the laws of the State of Texas,
          without regard to conflict-of-law rules. Venue for court proceedings, where applicable and not
          otherwise prohibited by law, shall be in the state or federal courts located in Texas.
        </p>
      </div>

      <div className="legal-section">
        <h2>18. Severability</h2>
        <p>
          If any provision of these Terms is found invalid, unlawful, or unenforceable, the remaining
          provisions remain in full force and effect. Any invalid provision shall be modified to the
          minimum extent necessary to make it valid and enforceable where permitted by law.
        </p>
      </div>

      <div className="legal-section">
        <h2>19. Updates to Terms</h2>
        <p>
          Casa de Candy may update these Terms at any time. Updates will be posted on this page with a
          revised “Last Updated” date. Continued use of the website or continued communication after
          updates constitutes acceptance of the updated Terms.
        </p>
      </div>

      <div className="legal-section">
        <h2>20. Contact</h2>
        <p>For questions about these Terms, contact: support@casadecandy.com.</p>
      </div>
    </LegalLayout>
  );
}
